import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { FaArrowRight, FaWhatsapp } from "react-icons/fa6";
import { HiOutlineMail } from "react-icons/hi";
import {
  FaLaptopFile,
  FaSchool,
  FaSchoolFlag,
  FaHandHoldingDollar,
  FaPassport,
} from "react-icons/fa6";
import Button from "../../../components/ui/Button";
import { ExtraItem } from "./ExtraItem";
import { ArrowUpNarrowWide, GraduationCap } from "lucide-react";

// Study abroad billing portal — all pricing CTAs route here
const STUDY_ABROAD_BILLING_URL =
  "https://billing-studyabroad.seamlessvisa.com/";

// ─── Types ─────────────────────────────────────────────────────────────────

interface Tier {
  id: string;
  badge: string;
  badgeVariant: "primary" | "secondary";
  name: string;
  tagline: string;
  price: number;
  features: string[];
  featured?: boolean;
  href: string;
  ctaVariant: "primary" | "outline";
}

interface AddonService {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  price?: string;
}

const TIERS: Tier[] = [
  {
    id: "basic",
    badge: "Starter",
    badgeVariant: "secondary",
    name: "Basic",
    tagline: "Ideal for focused, straightforward applications",
    price: 399,
    features: [
      "Educational Development Advisory",
      "School selection guidance",
      "Application submission (up to 3 schools)",
      "Document preparation guidance",
      "Status updates throughout the process",
    ],
    href: STUDY_ABROAD_BILLING_URL,
    ctaVariant: "outline",
  },
  {
    id: "standard",
    badge: "Most Popular",
    badgeVariant: "primary",
    name: "Standard",
    tagline: "Our most-chosen plan for competitive applicants",
    price: 499,
    features: [
      "All features of Basic",
      "Essay Writing & Personal Statement",
      "Recommendation Letter Counselling",
    ],
    featured: true,
    href: STUDY_ABROAD_BILLING_URL,
    ctaVariant: "primary",
  },
  {
    id: "premium",
    badge: "Full Service",
    badgeVariant: "secondary",
    name: "Premium",
    tagline: "End-to-end support including your visa journey",
    price: 750,
    features: [
      "All features of Standard",
      "Visa Application service (primary applicant)",
    ],
    href: STUDY_ABROAD_BILLING_URL,
    ctaVariant: "outline",
  },
];

const ADDON_SERVICES: AddonService[] = [
  { icon: FaLaptopFile, title: "Ivy League Applications", price: "$100" },
  { icon: FaSchool, title: "Additional Schools", price: "$50 / school" },
  {
    icon: FaSchoolFlag,
    title: "Additional Ivy League Schools",
    price: "$75 / school",
  },
  { icon: FaHandHoldingDollar, title: "Scholarship Assistance", price: "$150" },
  { icon: FaPassport, title: "Visa Application Service for Dependents" },
];

// ─── Sub-components ─────────────────────────────────────────────────────────

interface TierCardProps {
  tier: Tier;
  index: number;
}

const TierCard: React.FC<TierCardProps> = ({ tier, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.12 }}
    className={`relative flex flex-col rounded-2xl bg-white p-8 transition-all duration-300 ${
      tier.featured
        ? "border-2 border-blue-00 shadow-lg shadow-blue-00/10 ring-4 ring-blue-00/10 hover:-translate-y-1.5 hover:scale-[1.01] hover:shadow-xl hover:shadow-blue-00/15"
        : "border border-grey-eb hover:-translate-y-1 hover:border-blue-00/30 hover:shadow-lg hover:shadow-black/5"
    }`}
  >
    {/* Featured label */}
    {/* {tier.featured && (
      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-00 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
        Most Popular
      </span>
    )} */}

    {/* Badge */}
    <div className="absolute w-full justify-center -top-4 left-0 flex">
      <div className="">
        <span
          className={`rounded-full px-5 py-1.5 text-[11px] font-bold uppercase tracking-widest ${
            tier.badgeVariant === "primary"
              ? "bg-blue-00 text-white"
              : "bg-[#E0EDFF] text-blue-02"
          }`}
        >
          {tier.badge}
        </span>
      </div>
    </div>

    {/* Name + tagline */}
    <h3 className="mb-1 text-center text-2xl text-black-1a">{tier.name}</h3>
    <p className="mb-6 min-h-10! text-center text-xs leading-snug text-dark-6b">
      {tier.tagline}
    </p>

    {/* Price */}
    <div className="mb-6 text-center">
      <span className="text-5xl font-light tracking-tight text-black-1a">
        ${tier.price}
      </span>
      <p className="mt-1 text-xs text-dark-6b">one-time</p>
    </div>

    <div className="mb-5 border-b border-grey-eb" />

    {/* Features */}
    <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-dark-6b">
      What&apos;s included
    </p>
    <ul className="flex-1 space-y-0">
      {tier.features.map((feature) => (
        <li
          key={feature}
          className="flex items-start gap-2.5 border-b border-grey-eb/50 py-2.5 text-sm leading-snug text-dark-6b last:border-0"
        >
          <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-blue-00" />
          {feature.startsWith("All features of") ? (
            <span className="font-bold text-black-31">{feature}</span>
          ) : (
            feature
          )}
        </li>
      ))}
    </ul>

    {/* CTA */}
    <div className="mt-7">
      <Button
        label="Get Started"
        onClick={() => window.open(tier.href, "_blank")}
        variant={tier.ctaVariant === "primary" ? "primary" : "secondary"}
        endIcon={<FaArrowRight />}
      />
    </div>
  </motion.div>
);

interface AddonCardProps {
  service: AddonService;
  index: number;
}

const AddonCard: React.FC<AddonCardProps> = ({
  service: { icon: Icon, title, price },
}) => (
  <div className="group flex items-center gap-4 shrink-0">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-ea text-blue-00 transition-transform duration-300 group-hover:scale-110">
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex flex-col whitespace-nowrap">
      <span className="text-sm font-bold text-black-1a leading-snug">
        {title}
      </span>
      {price && (
        <span className="text-xs font-semibold text-blue-00 leading-snug">
          {price}
        </span>
      )}
    </div>
  </div>
);

// ─── Main component ─────────────────────────────────────────────────────────

const Packages: React.FC = () => {
  return (
    <section
      id="packages-section"
      className="relative overflow-hidden bg-linear-to-b from-blue-ea to-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* ── Section Header ───────────────────────────────── */}
        <div className="mx-auto mb-16 max-w-4xl text-center">
          <h2 className="mb-5 text-4xl font-light tracking-tight text-black-1a md:text-5xl">
            Find the{" "}
            <span className="text-blue-00 italic">Perfect Fit</span>{" "}
            for Your Success
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-dark-6b">
            Our packages give your international application a competitive edge.
            We combine strategic admissions coaching with global visa support to
            handle everything from compelling essays to immigration
            requirements.
          </p>
        </div>

        {/* ── Pricing Cards ────────────────────────────────── */}
        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {TIERS.map((tier, index) => (
            <TierCard key={tier.id} tier={tier} index={index} />
          ))}
        </div>

        {/* ── Extras ───────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-20 p-8 md:p-10"
        >
          <div className="grid gap-12 rounded-[28px] border border-blue-ea! bg-white p-10 md:grid-cols-[2fr_3fr] place-items-center">
            {/* Left */}
            <div>
              <h2 className="mb-2 font-lato text-[1.8rem] font-bold text-black-1a">
                Extras
              </h2>

              <p className="max-w-md text-sm leading-relaxed text-dark-6b">
                Additional fees that apply to specialized application types
                requiring more attention.
              </p>
            </div>

            {/* Right */}
            <div className="flex flex-col gap-4">
              <ExtraItem
                title="High-Touch Cases"
                fee="selected package fee + $100"
                tooltip='Complex or non-standard applications require a "high-touch" approach. This fee covers the technical complexity and extra time required to process your application file for success.'
                icon={<ArrowUpNarrowWide className="h-4 w-4" />}
              />

              <ExtraItem
                icon={<GraduationCap className="h-4 w-4 text-green-500" />}
                title="PhD Applications"
                fee="selected package fee + $100"
              />
            </div>
          </div>
        </motion.div>

        {/* ── Add-On Services ──────────────────────────────── */}
        <div className="pt-16 mb-20">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-light tracking-tight text-black-1a md:text-4xl">
              <span className="text-blue-00 italic mr-2">Customize</span>{" "}
              Your Application
            </h2>
            <p className="mx-auto max-w-2xl text-base leading-relaxed text-dark-6b">
              Choose from Ivy League application support, additional school
              applications (including more Ivy League options), and scholarship
              assistance (up to 3 programs) to maximize your opportunities.
            </p>
          </div>

          {/* Marquee Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full border-t border-b border-grey-eb py-6 bg-white/50 backdrop-blur-sm"
          >
            <div className="flex items-center w-full">
              {/* Static Left Side Container */}
              <div className="flex-none px-6 lg:pl-8 lg:pr-12 border-r border-grey-eb flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-blue-ea flex items-center justify-center">
                  <span className="text-blue-00 text-lg">✨</span>
                </div>
                <p className="hidden md:block text-sm font-bold text-black-1a tracking-wide uppercase">
                  Add-Ons
                </p>
              </div>

              {/* Sliding Right Side Container */}
              <div className="flex-1 overflow-hidden pointer-events-none">
                <Marquee
                  gradient={false}
                  speed={20}
                  className="overflow-hidden"
                >
                  <div className="flex items-center gap-16 pr-16">
                    {ADDON_SERVICES.map((service) => (
                      <AddonCard
                        key={service.title}
                        service={service}
                        index={0}
                      />
                    ))}
                    {/* Repeated buffer to ensure smooth scrolling */}
                    {ADDON_SERVICES.map((service) => (
                      <AddonCard
                        key={`${service.title}-repeat`}
                        service={service}
                        index={0}
                      />
                    ))}
                  </div>
                </Marquee>
              </div>
            </div>
          </motion.div>
        </div>
        {/* Custom Advisory Callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="relative overflow-hidden rounded-2xl bg-blue-02 p-8 md:p-10 md:w-11/12 mx-auto"
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-blue-00/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-[#00C1A3]/15 blur-2xl" />

          <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-center">
            <div className="flex-1">
              <h3 className="mb-2.5 text-2xl font-bold text-white md:text-3xl">
                Need Personalized Visa Support?
              </h3>
              <p className="max-w-xl text-sm leading-relaxed text-white/75 md:text-base">
                We tailor custom solutions to your unique needs. Whether
                it&apos;s a complex case or a specialized requirement, our team
                is ready to help you succeed.
              </p>
            </div>

            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <a
                href="https://wa.me/2347073254161"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contact via WhatsApp"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/20"
              >
                <FaWhatsapp className="h-4 w-4" />
                WhatsApp
              </a>
              <a
                href="mailto:hello@seamlessvisa.com"
                aria-label="Contact via Email"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-blue-02 transition-all duration-300 hover:bg-[#EAF5FF]"
              >
                <HiOutlineMail className="h-4 w-4" />
                Email Us
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Packages;
